import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  SegmentDeepDiveResponse,
  fetchAllScenarios,
  fetchOutputRGMSegmentDeepDive,
  fetchOutputFinanceSegmentDeepDive,
  fetchDeepDiveDownloadDataAPI,
} from './outputSegmentDeepDiveAPI';
import { RootState } from '../../state/store';
import { hideLoader, showLoader } from '../ui/loaderSlice';
import { enableMapSet } from 'immer';

enableMapSet();

export interface OutputSegmentDeepDiveState {
  isDeepdiveVisible: boolean;
  segments: SegmentDeepDiveResponse[];
  loading: boolean;
  error: string | null;
  expandedRows: Set<string>;
  sortDirection: 'asc' | 'desc';
  orderBy: keyof SegmentDeepDiveResponse | null;
  filterText: string;
  anchorEl: any;
  selectedScenarios: {
    base: string;
    child: string;
  };
  activeMenu: string | null;
  scenarios: string[];
  selectedDeepdiveToggle: string;
}

const initialState: OutputSegmentDeepDiveState = {
  isDeepdiveVisible: false,
  segments: [],
  loading: false,
  error: null,
  expandedRows: new Set(),
  sortDirection: 'asc',
  orderBy: null,
  filterText: '',
  anchorEl: null,
  selectedScenarios: {
    base: '',
    child: '',
  },
  activeMenu: null,
  scenarios: [],
  selectedDeepdiveToggle: 'rgm',
};

export const fetchSegmentDeepDive = createAsyncThunk<SegmentDeepDiveResponse[], any>(
  'outputSegmentDeepDive/fetchSegmentDeepDive',
  async (payload: any, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const selectedToggleVal = state.outputSegmentDeepDive.selectedDeepdiveToggle;
      if (selectedToggleVal === 'rgm') {
        const response = await fetchOutputRGMSegmentDeepDive(payload);
        return response;
      } else {
        const response = await fetchOutputFinanceSegmentDeepDive(payload);
        return response;
      }
    } catch (error) {
      dispatch(hideLoader());
      throw error;
    }
  },
);

export const fetchScenarios = createAsyncThunk<string[], void>(
  'outputSegmentDeepDive/fetchScenarios',
  async (_, { dispatch }) => {
    try {
      const scenarios = await fetchAllScenarios();
      return scenarios;
    } catch (error) {
      dispatch(hideLoader());
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  },
);

export const fetchDeepDiveDownloadData = createAsyncThunk(
  'outputSegmentDeepDive/fetchDeepDiveDownloadData',
  async (payload: any, { dispatch }) => {
    try {
      dispatch(showLoader());
      const response = await fetchDeepDiveDownloadDataAPI(payload);
      return response;
    } catch (error) {
      dispatch(hideLoader());
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  },
);

const outputSegmentDeepDiveSlice = createSlice({
  name: 'outputSegmentDeepDive',
  initialState,
  reducers: {
    setExpandedRows(state, action: PayloadAction<string>) {
      const segment = action.payload;
      if (state.expandedRows.has(segment)) {
        state.expandedRows.delete(segment);
      } else {
        state.expandedRows.add(segment);
      }
    },
    setSortDirection(state, action: PayloadAction<'asc' | 'desc'>) {
      state.sortDirection = action.payload;
    },
    setOrderBy(state, action: PayloadAction<keyof SegmentDeepDiveResponse>) {
      state.orderBy = action.payload;
    },
    setFilterText(state, action: PayloadAction<string>) {
      state.filterText = action.payload;
    },
    setAnchorEl(state, action: PayloadAction<HTMLElement | null>) {
      state.anchorEl = action.payload;
    },
    setActiveMenu(state, action: PayloadAction<string | null>) {
      state.activeMenu = action.payload;
    },
    setScenarios(state, action: PayloadAction<string[]>) {
      state.scenarios = action.payload;
    },
    setSelectedScenario(state, action: PayloadAction<{ type: 'base' | 'child'; scenario: string }>) {
      const { type, scenario } = action.payload;
      state.selectedScenarios[type] = scenario;
    },
    setDeepdiveVisible(state, action: PayloadAction<boolean>) {
      state.isDeepdiveVisible = action.payload;
    },
    setDeepdiveToggle: (state, action: PayloadAction<string>) => {
      state.selectedDeepdiveToggle = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSegmentDeepDive.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.segments = [];
    });

    builder.addCase(fetchSegmentDeepDive.fulfilled, (state, action: PayloadAction<SegmentDeepDiveResponse[]>) => {
      state.loading = false;
      state.segments = action.payload;
    });

    builder.addCase(fetchSegmentDeepDive.rejected, (state, action) => {
      state.loading = false;
      state.segments = [];
      state.error = action.error.message || 'Failed to fetch deep dive data';
    });

    builder.addCase(fetchScenarios.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(fetchScenarios.fulfilled, (state, action: PayloadAction<string[]>) => {
      state.loading = false;
      state.scenarios = action.payload;
    });

    builder.addCase(fetchScenarios.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch scenarios';
    });
  },
});

export const {
  setExpandedRows,
  setSortDirection,
  setOrderBy,
  setFilterText,
  setAnchorEl,
  setActiveMenu,
  setSelectedScenario,
  setScenarios,
  setDeepdiveVisible,
  setDeepdiveToggle,
} = outputSegmentDeepDiveSlice.actions;

export const selectDeepDiveSegments = (state: RootState) => state.outputSegmentDeepDive.segments;
export const selectDeepDiveLoading = (state: RootState) => state.outputSegmentDeepDive.loading;
export const selectDeepDiveError = (state: RootState) => state.outputSegmentDeepDive.error;
export const selectExpandedRows = (state: RootState) => state.outputSegmentDeepDive.expandedRows;
export const selectSortDirection = (state: RootState) => state.outputSegmentDeepDive.sortDirection;
export const selectOrderBy = (state: RootState) => state.outputSegmentDeepDive.orderBy;
export const selectFilterText = (state: RootState) => state.outputSegmentDeepDive.filterText;
export const selectAnchorEl = (state: RootState) => state.outputSegmentDeepDive.anchorEl;
export const selectSelectedScenarios = (state: RootState) => state.outputSegmentDeepDive.selectedScenarios;
export const selectActiveMenu = (state: RootState) => state.outputSegmentDeepDive.activeMenu;
export const selectScenarios = (state: RootState) => state.outputSegmentDeepDive.scenarios;
export const selectIsDeepdiveVisible = (state: RootState) => state.outputSegmentDeepDive.isDeepdiveVisible;

export default outputSegmentDeepDiveSlice.reducer;
